
import React, { createContext, useContext, useState, useEffect } from 'react';
import { enhancedToast } from '@/utils/notifications/enhancedToast';
import { playNotificationSound } from '../password/utils';
import { Notification, NotificationType, NotificationPriority, NotificationContextType } from './types';
import { getNotificationPriority, generateId } from './notificationUtils';
import { defaultToastConfig } from '@/utils/toastConfig';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Load notifications from localStorage on mount
  useEffect(() => {
    const savedNotifications = localStorage.getItem('notifications');
    if (savedNotifications) {
      try {
        setNotifications(JSON.parse(savedNotifications));
      } catch (e) {
        console.error('Failed to parse notifications from localStorage');
      }
    }
  }, []);

  // Save notifications to localStorage when they change
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Add a new notification
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: generateId(),
      timestamp: Date.now(),
      read: false,
      priority: notification.priority || getNotificationPriority(3, notification.type),
    };

    setNotifications(prev => [newNotification, ...prev]);
    
    // Show toast with enhanced sound based on notification type
    if (notification.type === 'completion' || notification.type === 'certificate') {
      enhancedToast.success(notification.title, notification.message);
    } else if (notification.type === 'system-error') {
      enhancedToast.error(notification.title, notification.message);
    } else if (notification.type === 'reminder') {
      enhancedToast.warning(notification.title, notification.message);
    } else {
      enhancedToast.info(notification.title, notification.message);
    }
    
    // Play notification sound (backup for older contexts)
    playNotificationSound();
  };

  // Mark notification as read
  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  // Clear all notifications
  const clearNotifications = () => {
    setNotifications([]);
  };

  // Process payment action
  const processPaymentAction = (notificationId: string) => {
    // Find the notification
    const notification = notifications.find(n => n.id === notificationId);
    
    if (!notification) {
      enhancedToast.error("Notification not found");
      return;
    }
    
    // Mark the notification as read
    markAsRead(notificationId);
    
    // Navigate to the payment page or perform other actions based on the metadata
    if (notification.metadata?.actionUrl) {
      // In a real implementation, this might use a router to navigate
      window.location.href = notification.metadata.actionUrl;
    } else if (notification.studentId) {
      // Default payment page if no specific URL is provided
      window.location.href = `/students/${notification.studentId}/payment`;
    }
    
    enhancedToast.success("Processing payment...", "Redirecting to payment page");
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        unreadCount,
        processPaymentAction,
        getNotificationPriority
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Export types for backward compatibility
export type { Notification, NotificationType, NotificationPriority };
